import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { STRIPE_URL } from "@/lib/links";
import Reveal from "@/components/site/Reveal";
import { track } from "@/lib/analytics";

const STEPS = [
  {
    n: "01",
    title: "Accedes al diagnóstico",
    body: "Tras el pago, acceso inmediato al formulario. Se completa desde cualquier dispositivo en 15 minutos.",
  },
  {
    n: "02",
    title: "Respondes 30 preguntas",
    body: "Cada pregunta refleja situaciones reales de gestión de talento. No hay respuestas correctas, solo respuestas honestas.",
  },
  {
    n: "03",
    title: "Recibes tus resultados",
    body: "Al terminar obtienes tu puntuación global, tu puntuación por cada área y la interpretación profesional de cada resultado.",
  },
  {
    n: "04",
    title: "Sabes por dónde empezar",
    body: "Con el diagnóstico en la mano, tienes claridad sobre qué está funcionando, qué necesita atención y qué es urgente.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="metodo"
      data-testid="how-section"
      className="relative bg-[#F7F9F8] text-[#06231D] py-28 lg:py-40 grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 lg:mb-24">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="font-serif-italic text-[#085041] text-[9rem] lg:text-[12rem] leading-none">04</span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#F7F9F8] bg-[#06231D] px-3 py-2 rounded-md">Cómo funciona</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[20ch] mx-auto">
              Simple, rápido y <span className="font-serif-italic text-[#085041] font-normal">100% online</span>.
            </h2>
          </Reveal>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting horizontal line (desktop) */}
          <div className="hidden lg:block absolute top-[52px] left-[8%] right-[8%] h-px bg-[#06231D]/15">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
              style={{ originX: 0 }}
              className="h-full brand-gradient"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group relative"
                data-testid={`step-${i + 1}`}
              >
                <div className="relative w-[104px] h-[104px] mb-8">
                  <span aria-hidden className="absolute -inset-2 rounded-full border border-[#C9A84C]/50 group-hover:border-[#C9A84C] transition-colors duration-500" />
                  <span aria-hidden className="absolute -inset-2 rounded-full border-t border-[#C9A84C] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10 w-full h-full rounded-full brand-gradient shadow-lg shadow-[#085041]/20 grid place-items-center group-hover:scale-105 transition-transform duration-500">
                    <span className="font-serif-italic text-4xl text-white">{s.n}</span>
                  </div>
                </div>
                <span aria-hidden className="block w-8 h-px bg-[#C9A84C] mb-4" />
                <h3 className="font-display text-xl lg:text-2xl font-medium leading-tight mb-3 max-w-[18ch]">{s.title}</h3>
                <p className="text-[#06231D]/65 text-[15px] leading-relaxed font-light max-w-[38ch] text-justify hyphens-auto">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-20 lg:mt-28 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <a
              href={STRIPE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="how-cta-button"
              onClick={() => track("cta_click", { location: "how_it_works", product: "diagnostic" })}
              className="group inline-flex items-center gap-3 px-8 h-16 rounded-full brand-gradient text-white text-base font-medium cta-lift"
            >
              Empieza el diagnóstico
              <span className="w-9 h-9 rounded-full bg-white/15 grid place-items-center group-hover:bg-white/25 transition-colors">
                <ArrowUpRight size={18} strokeWidth={1.5} />
              </span>
            </a>
            <div className="text-[12px] uppercase tracking-[0.2em] text-[#06231D]/50 max-w-[240px]">
              Resultado en 15 minutos. Sin llamadas. Sin fricción.
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
